import React from 'react';
import { Box, Drawer, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { HelpArticle, HelpModule, HelpSubModule } from '../../types/help';
import HelpSidebar from './HelpSidebar';
import { DFT_BRAND } from '../../styles/brandPalette';

export interface HelpLayoutProps {
  modules: HelpModule[];
  subModulesByModuleId: Record<string, HelpSubModule[]>;
  articlesBySubModuleId: Record<string, HelpArticle[]>;
  expandedModuleIds: Record<string, boolean>;
  expandedSubModuleIds: Record<string, boolean>;
  loadingSubModuleModuleIds: Record<string, boolean>;
  loadingArticlesSubModuleIds: Record<string, boolean>;
  selectedModuleId: string;
  selectedSubModuleId: string;
  selectedArticleId: string;
  loadingModules: boolean;
  onModuleNavigate: (moduleId: string) => void;
  onToggleModuleExpand: (moduleId: string) => void;
  onToggleSubModuleExpand: (subModuleId: string) => void;
  onSubModuleNavigate: (moduleId: string, subModuleId: string) => void;
  onArticleNavigate: (moduleId: string, subModuleId: string, articleId: string) => void;
  onOpenSearch: () => void;
  sidebarSearchQuery: string;
  onSidebarSearchChange: (q: string) => void;
  mobileNavOpen: boolean;
  onCloseMobileNav: () => void;
  children: React.ReactNode;
}

/** Narrower on 1366 / 14" Mac; fuller from xl. */
const NAV_WIDTH = { xs: 260, md: 220, lg: 248, xl: 280 } as const;

const HelpLayout: React.FC<HelpLayoutProps> = ({
  modules,
  subModulesByModuleId,
  articlesBySubModuleId,
  expandedModuleIds,
  expandedSubModuleIds,
  loadingSubModuleModuleIds,
  loadingArticlesSubModuleIds,
  selectedModuleId,
  selectedSubModuleId,
  selectedArticleId,
  loadingModules,
  onModuleNavigate,
  onToggleModuleExpand,
  onToggleSubModuleExpand,
  onSubModuleNavigate,
  onArticleNavigate,
  onOpenSearch,
  sidebarSearchQuery,
  onSidebarSearchChange,
  mobileNavOpen,
  onCloseMobileNav,
  children,
}) => {
  const isMdUp = useMediaQuery('(min-width:900px)');

  const sidebar = (
    <HelpSidebar
      modules={modules}
      subModulesByModuleId={subModulesByModuleId}
      articlesBySubModuleId={articlesBySubModuleId}
      expandedModuleIds={expandedModuleIds}
      expandedSubModuleIds={expandedSubModuleIds}
      loadingSubModuleModuleIds={loadingSubModuleModuleIds}
      loadingArticlesSubModuleIds={loadingArticlesSubModuleIds}
      selectedModuleId={selectedModuleId}
      selectedSubModuleId={selectedSubModuleId}
      selectedArticleId={selectedArticleId}
      loadingModules={loadingModules}
      onModuleNavigate={onModuleNavigate}
      onToggleModuleExpand={onToggleModuleExpand}
      onToggleSubModuleExpand={onToggleSubModuleExpand}
      onSubModuleNavigate={onSubModuleNavigate}
      onArticleNavigate={onArticleNavigate}
      onOpenSearch={onOpenSearch}
      sidebarSearchQuery={sidebarSearchQuery}
      onSidebarSearchChange={onSidebarSearchChange}
    />
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        height: '100%',
        minHeight: 0,
        minWidth: 0,
        overflow: 'hidden',
        bgcolor: DFT_BRAND.bg,
      }}
    >
      {isMdUp ? (
        <Paper
          elevation={0}
          sx={{
            width: NAV_WIDTH,
            minWidth: { md: 220, lg: 248, xl: 280 },
            maxWidth: NAV_WIDTH,
            flexShrink: 0,
            height: '100%',
            minHeight: 0,
            borderRight: `1px solid ${DFT_BRAND.line}`,
            borderRadius: 0,
            bgcolor: DFT_BRAND.paper,
            overflow: 'hidden',
          }}
        >
          {sidebar}
        </Paper>
      ) : (
        <Drawer
          open={mobileNavOpen}
          onClose={onCloseMobileNav}
          PaperProps={{
            sx: {
              width: { xs: 'min(280px, 85vw)', sm: 260 },
              bgcolor: DFT_BRAND.paper,
            },
          }}
        >
          {sidebar}
        </Drawer>
      )}

      <Box
        sx={{
          flex: 1,
          height: '100%',
          minWidth: 0,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: DFT_BRAND.bg,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default HelpLayout;
